import React, { useState } from 'react';
import { Alert, Button, Col, Label, Panel, Row } from 'react-bootstrap';
import { CenterAddressType } from '@nkr/enums/center-address-type';
import { IStrongBoxComplianceItem } from '@nkr/models/center-performance';
import { StrongBoxComplianceType } from '@nkr/enums/strong-box-compliance-type';
import useCenterAddress from '@nkr/store/center/hooks/use-center-address';
import useStrongBoxAddrApproval from '@nkr/store/strong-box/hooks/use-strong-box-addr-approval';
import CenterAddressAddEdit from '@nkr/views/center-profile/addresses/add-edit';
import AddressConfirmationButton from './address-confirmation-button';

interface IProps {
    item: IStrongBoxComplianceItem,
    onItemResolved: (item: IStrongBoxComplianceItem) => void
}

const AddressCompliancePanel = ({ item, onItemResolved }: IProps) => {
    const [showEditor, setShowEditor] = useState<boolean>(false);
    const { loading, error, address, fetchData } = useCenterAddress(item.centerId, CenterAddressType.StrongBoxShipTo)
    const { error: approvalError, loading: approvalLoading, approve } = useStrongBoxAddrApproval(() => {
        onItemResolved(item);
    });

    const editAddress = () => {
        setShowEditor(true);
    }

    const cancelEdit = () => {
        setShowEditor(false);
    }

    const handleAddressUpdated = () => {
        setShowEditor(false);

        fetchData(item.centerId, CenterAddressType.StrongBoxShipTo);
        approve(item.strongBoxCenterId);
        onItemResolved(item);
    }

    let body = (
        <>
            <Row>
                <Col sm={12}>
                    {!address && item.type == StrongBoxComplianceType.AddressMissing &&
                        <>
                            <Alert bsStyle='danger' bsSize='sm'>
                                A StrongBox shipping address is required.
                            </Alert>

                            <Button bsStyle='primary' onClick={() => setShowEditor(true)}>Add Shipping Address</Button>
                        </>
                    }

                    {address && item.type == StrongBoxComplianceType.AddressConfirmation &&
                        <>
                            <Alert bsSize='sm'>
                                {address.label && <span>{address.label}<br /></span>}

                                <span>{address.firstName} {address.lastName}</span><br />

                                <span>{address.shipToPhoneNumber}</span><br />

                                {address.addressLine1 && <span>{address.addressLine1}<br /></span>}

                                {address.addressLine2 && <span>{address.addressLine2}<br /></span>}

                                <span>{address.city}, {address.stateAbbreviation} {address.postalCode}<br /></span>
                            </Alert>

                            <Button bsStyle='primary' onClick={() => editAddress()}>Update Shipping Address</Button> &nbsp;
                            <AddressConfirmationButton item={item} onItemResolved={onItemResolved} />
                        </>
                    }

                    {address && item.type == StrongBoxComplianceType.AddressMissingInformation &&
                        <>
                            <Alert bsSize='sm' bsStyle='danger'>
                                {address.label && <span>{address.label}<br /></span>}

                                {!address.firstName && !address.lastName && (
                                    <><Label bsStyle='danger' style={{ fontSize: '90%' }}>First Name and Last Name are missing</Label><br /></>
                                )}

                                {!address.firstName && address.lastName && (
                                    <><Label bsStyle='danger' style={{ fontSize: '90%' }}>First Name is missing</Label><br /></>
                                )}

                                {address.firstName && !address.lastName && (
                                    <><Label bsStyle='danger' style={{ fontSize: '90%' }}>Last Name is missing</Label><br /></>
                                )}

                                {address.firstName && address.lastName &&
                                    <><span>{address.firstName} {address.lastName}</span><br /></>
                                }

                                {!address.shipToPhoneNumber ?
                                    <><Label bsStyle='danger' style={{ fontSize: '90%' }}>Ship To Phone Number is missing</Label><br /></>
                                    :
                                    <><span>{address.shipToPhoneNumber}</span><br /></>
                                }

                                {address.addressLine1 && <span>{address.addressLine1}<br /></span>}

                                {address.addressLine2 && <span>{address.addressLine2}<br /></span>}

                                <span>{address.city}, {address.stateAbbreviation} {address.postalCode}<br /></span>
                            </Alert>

                            <Button bsStyle='primary' onClick={() => editAddress()}>Update Shipping Address</Button> &nbsp;
                        </>
                    }
                </Col>
            </Row>
        </>
    );

    if (loading) {
        body = (<Row><Col sm={12}><Alert bsSize='sm'>Loading ...</Alert></Col></Row>);
    }

    if (error) {
        body = (<Row><Col sm={12}><Alert bsStyle='danger' bsSize='sm'>{error}</Alert></Col></Row>);
    }

    return (
        <>
            <Panel>
                <Panel.Heading>Shipping Compliance</Panel.Heading>
                <Panel.Body>
                    <p>{item.description}</p>
                    {body}
                </Panel.Body>
            </Panel>

            <CenterAddressAddEdit
                show={showEditor}
                centerId={item.centerId}
                addressId={address ? address.id : 0}
                allowedAddressTypes={[CenterAddressType.StrongBoxShipTo]}
                onUpdated={handleAddressUpdated}
                onCancel={cancelEdit}
            />
        </>
    );
}

export default AddressCompliancePanel;